import React from 'react';
import {
  CartesianGrid,
  ComposedChart,
  Label,
  LabelList,
  Area,
  ResponsiveContainer,
  Scatter,
  XAxis,
  YAxis,
} from 'recharts';
import { useAxisStyles, useChartColors } from 'src/hooks/colors/useChartColors';
import { useLegendContext } from '../../Legend/LegendContext';

export type AreaScatterDataType<A> = {
  name: string;
  data: A[];
  points: A[];
};

export type AreaScatterGraphProps<A> = {
  data: AreaScatterDataType<A>[];
  xAxisLabel?: string;
  yAxisLabel?: string;
  dataKeys: {
    xAxis: string;
    yAxis: string;
  };

  xAxisFormatter?: (value: number | string) => string;
  yAxisFormatter?: (value: number) => string;
  children?: JSX.Element;
};

export function AreaScatterGraph<A>({
  data,
  dataKeys,
  xAxisLabel,
  yAxisLabel,

  xAxisFormatter,
  yAxisFormatter,
  children,
}: AreaScatterGraphProps<A>) {
  const { keyToSelected, keyToColor } = useLegendContext();

  const { fontColor, gridColor } = useChartColors();
  const axisStyles = useAxisStyles();

  return (
    <ResponsiveContainer>
      <ComposedChart>
        <CartesianGrid
          strokeDasharray="0"
          strokeWidth={1}
          stroke={gridColor}
          strokeOpacity={0.4}
          vertical={false}
          fill="transparent"
        />
        <YAxis
          dataKey={dataKeys.yAxis}
          type="number"
          width={70}
          {...axisStyles}
          axisLine={false}
          tickFormatter={yAxisFormatter}>
          {yAxisLabel && (
            <Label
              dy={50}
              offset={-15}
              angle={-90}
              style={{ fill: fontColor }}
              value={yAxisLabel}
              position="insideLeft"
            />
          )}
        </YAxis>
        <XAxis
          dataKey={dataKeys.xAxis}
          type="number"
          tickFormatter={xAxisFormatter}
          {...axisStyles}
          axisLine={false}>
          {xAxisLabel && (
            <Label
              style={{ fill: fontColor }}
              value={xAxisLabel}
              offset={-20}
              position="insideBottom"
            />
          )}
        </XAxis>

        {data.map((k: AreaScatterDataType<A>) => {
          if (!keyToSelected[k.name]) {
            return null;
          }

          return (
            <React.Fragment key={k.name}>
              <defs>
                <linearGradient
                  id={`colorPv_${k.name}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1">
                  <stop stopColor={keyToColor[k.name]} stopOpacity="0.2" />
                  <stop
                    offset="1"
                    stopColor={keyToColor[k.name]}
                    stopOpacity="0.05"
                  />
                </linearGradient>
              </defs>
              <Area
                isAnimationActive={false}
                activeDot={false}
                name={k.name}
                data={k.data}
                dataKey={dataKeys.yAxis}
                fill={`url(#colorPv_${k.name})`}
                stroke={keyToColor[k.name]}
                dot={false}
                strokeWidth={2}
                type="monotone"
              />
            </React.Fragment>
          );
        })}
        {data.map((k: AreaScatterDataType<A>) => {
          if (!keyToSelected[k.name]) {
            return null;
          }

          const color = keyToColor[k.name];

          return (
            <Scatter
              isAnimationActive={false}
              name={`${k.name}_points`}
              key={`${k.name}_points`}
              data={k.points}
              dataKey={dataKeys.yAxis}
              fill={color}
              legendType="none">
              <LabelList
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                data={k.points as any}
                dataKey={dataKeys.xAxis}
                position="top"
                fill={color}
                offset={12}
              />
            </Scatter>
          );
        })}
        {children}
      </ComposedChart>
    </ResponsiveContainer>
  );
}
